import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { useSubmittingMutation } from "@/design-system/components/ui";
import { usePortalAuth } from "../PortalAuthContext";
import { portalImpersonationApi } from "../api/impersonation";

// Admin User Impersonation — the portal-side banner. Mirrors the staff
// ImpersonationBanner in shape and loudness, but is a portal-local component:
// it reads the portal session (usePortalAuth), posts to the PORTAL exit
// endpoint, and returns the admin to the portal sign-in page. It imports no
// staff module and no React Query, per portal/isolation.test.ts.
//
// Renders in EVERY PortalChrome branch, including the disabled-account
// short-circuit — seeing what a disabled account sees is a legitimate reason to
// impersonate, so the exit control has to survive that page.

/** Formats a remaining duration as `h:mm:ss` (or `m:ss` under an hour). */
function formatRemaining(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "expired";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

function useCountdown(expiresAt: string | null | undefined): string | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return;
    const handle = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [expiresAt]);

  if (!expiresAt) return null;
  const target = Date.parse(expiresAt);
  if (Number.isNaN(target)) return null;
  return formatRemaining(target - now);
}

export function PortalImpersonationBanner() {
  const auth = usePortalAuth();
  const impersonation = auth.me?.impersonation ?? null;
  const countdown = useCountdown(impersonation?.expiresAt);

  // No `refresh`: the session identity changes underneath everything, so this
  // ends in a full page navigation rather than a refetch of the old identity.
  const exit = useSubmittingMutation(() => portalImpersonationApi.exit(), {
    onSuccess: () => window.location.assign("/portal/login"),
  });

  if (!impersonation) return null;

  const message =
    exit.error instanceof Error
      ? exit.error.message
      : exit.error
        ? "Could not end the impersonated session."
        : null;

  return (
    <div
      role="status"
      data-testid="portal-impersonation-banner"
      className="w-full bg-status-warning text-onyx border-b-2 border-gold-shadow px-space-4 py-space-3"
    >
      <div className="flex flex-wrap items-center gap-space-3">
        <span className="inline-flex items-center gap-space-2 font-display font-bold uppercase tracking-label">
          <Eye size={16} strokeWidth={2} aria-hidden />
          <span className="text-label">Impersonating</span>
        </span>

        <span className="font-semibold">
          <span className="text-small">{impersonation.targetLabel}</span>
        </span>

        <span className="text-onyx/80">
          <span className="text-caption">
            Signed in as {impersonation.impersonatorDisplayName}
            {countdown ? ` · ${countdown} remaining` : null}
          </span>
        </span>

        <button
          type="button"
          onClick={() => void exit.submit()}
          disabled={exit.pending}
          className={
            "ml-auto inline-flex items-center rounded-md bg-onyx px-space-3 py-space-2 " +
            "font-display font-bold uppercase tracking-button text-gold " +
            "hover:bg-onyx/90 focus-visible:outline-none focus-visible:shadow-goldRing " +
            "disabled:opacity-40 disabled:cursor-not-allowed"
          }
        >
          <span className="text-label">
            {exit.pending ? "Exiting…" : "Exit impersonation"}
          </span>
        </button>
      </div>

      {message && (
        <p role="alert" className="mt-space-2 mb-0 text-onyx">
          <span className="text-caption">{message}</span>
        </p>
      )}
    </div>
  );
}
